import type { SolrParams } from '#shared/solr'
import type { FormattedSearchResult, SearchContext, WithinDocumentSearchStrategy } from './strategies/types'

export function useSearchUI(strategy: WithinDocumentSearchStrategy, context: SearchContext, externalSearchMode?: Ref<boolean>) {
  // ================================================================================================
  // CORE STATE MANAGEMENT
  // ================================================================================================

  const route = useRoute()
  const searchTerm = ref('') // Immediate updates for UI responsiveness and URL sync
  const apiSearchTerm = ref('') // Debounced version used only for API calls
  const searchMode = externalSearchMode || ref(false) // External search mode if provided

  // ================================================================================================
  // QUERY DEFINITIONS (Executed in this order)
  // ================================================================================================

  // Debounce the search term for API calls to avoid excessive requests
  const debouncedUpdate = useDebounceFn((value: string) => {
    apiSearchTerm.value = value
  }, 400)

  // Build reactive query parameters based on debounced search term and context
  const queryParams = computed<SolrParams | null>(() => {
    const query = apiSearchTerm.value.trim()
    if (!query) {
      return null
    }
    return strategy.buildQueryParams(query, context)
  })

  // ================================================================================================
  // SOLR QUERY EXECUTION
  // ================================================================================================

  const { data: solrData, isLoading, isError } = useSolrQueryV2(
    queryParams,
    strategy.getCollectionKey(),
    strategy.getEndpoint(),
  )

  // ================================================================================================
  // DATA PROCESSING
  // ================================================================================================

  // Process search results reactively
  const results = computed<FormattedSearchResult[]>(() => {
    if (!solrData.value || !apiSearchTerm.value.trim()) {
      return []
    }

    return strategy.formatResults(solrData.value)
  })

  // `solrData` is undefined until the current search finishes. Only treat empty `results` as "no hits" once it is defined.
  const hasNoResults = computed(() => solrData.value !== undefined && results.value.length === 0)

  // ================================================================================================
  // EXECUTION SEQUENCE: INITIALIZATION & REACTIVE UPDATES
  // ================================================================================================

  // SEQUENCE 1: INITIAL SEARCH STATE (on mount)
  const initializeFromUrl = () => {
    const urlQuery = route.query.q as string
    if (urlQuery) {
      searchTerm.value = urlQuery
      apiSearchTerm.value = urlQuery // Initialize both for URL-based searches
      searchMode.value = true
    }
  }

  onMounted(() => {
    initializeFromUrl()
  })

  // SEQUENCE 2: USER INPUT HANDLING (when search term changes)
  watch(searchTerm, (newValue) => {
    if (newValue.trim()) {
      navigateTo({
        query: {
          ...route.query,
          q: newValue.trim(),
        },
      }, { replace: true })
      debouncedUpdate(newValue.trim())
    }
    else {
      navigateTo({
        query: {
          ...route.query,
          q: undefined,
        },
      }, { replace: true })
      debouncedUpdate('')
    }
  })

  // SEQUENCE 3: EXTERNAL URL CHANGES (mobile bar writes ?q= per keystroke).
  // Trimmed compare guards the loop: our own write-back is always the trimmed term.
  watch(() => route.query.q, (urlQuery) => {
    const value = typeof urlQuery === 'string' ? urlQuery : ''
    if (value === searchTerm.value.trim()) {
      return
    }
    searchTerm.value = value
  })

  return {
    searchMode,
    searchTerm,
    isLoading,
    isError,
    results,
    hasNoResults,
  }
}
